import { useEffect, useState, useCallback } from "react";
import Header from "@/components/Header";
import KpiBar from "@/components/KpiBar";
import TrendCards from "@/components/TrendCards";
import VideoGenerator from "@/components/VideoGenerator";
import VideoGallery from "@/components/VideoGallery";
import HistoryTabs from "@/components/HistoryTabs";
import BrandBanner from "@/components/BrandBanner";
import MonitoredBloggers from "@/components/MonitoredBloggers";
import { api } from "@/lib/api";
import { AppNav } from "@/components/AppNav";
import { Paywall } from "@/components/Paywall";
import { FacebookBanner } from "@/components/FacebookBanner";
import { MetaReconnectBanner } from "@/components/MetaReconnectBanner";
import { BloggersPanel } from "@/components/BloggersPanel";
import { isActive, useAuth } from "@/lib/auth";

export default function Dashboard() {
  const { user } = useAuth();
  if (!isActive(user)) return (<><AppNav current="dashboard" /><Paywall /></>);
  return <DashboardContent isAdmin={user.role === "admin"} />;
}

function DashboardContent({ isAdmin }) {
  const [agentName, setAgentName] = useState("");
  useEffect(() => { api.agent().then(a => setAgentName(a.name)).catch(() => {}); }, []);
  const [stats, setStats] = useState(null);
  const [todayTrends, setTodayTrends] = useState([]);
  const [allTrends, setAllTrends] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seedPrompt, setSeedPrompt] = useState("");
  const [seedTrend, setSeedTrend] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [s, tt, at, vs] = await Promise.all([
        api.stats(),
        api.trendsToday(),
        api.trends({ limit: 50 }),
        api.videos({ limit: 100 }),
      ]);
      setStats(s);
      setTodayTrends(tt);
      setAllTrends(at);
      setVideos(vs);
    } catch (e) {
      console.error("refresh error", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const usePromptFromTrend = (trend) => {
    setSeedPrompt(trend.voiceover || trend.hook || trend.title);
    setSeedTrend(trend);
    // scroll to generator
    document.getElementById("studio-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const publishedVideos = videos.filter(v => v.status === "published");

  return (
    <>
      <AppNav current="dashboard" />
      <Header onScanDone={refresh} isAdmin={isAdmin} agentName={agentName} />
      <main className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-10 py-8 space-y-6" data-testid="dashboard-main">
        {isAdmin && <BrandBanner stats={stats} />}
        <FacebookBanner />
        <MetaReconnectBanner />
        <BloggersPanel onCreated={refresh} refreshKey={`${allTrends.length}-${videos.length}`} />
        <KpiBar stats={stats} loading={loading} />

        <div id="studio-anchor" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <TrendCards trends={todayTrends} loading={loading && todayTrends.length === 0} onUsePrompt={usePromptFromTrend} onCreated={refresh} />
          </div>
          <div className="lg:col-span-5">
            <VideoGenerator seedPrompt={seedPrompt} seedTrend={seedTrend} onCreated={refresh} />
          </div>
        </div>

        <VideoGallery videos={videos} onRefresh={refresh} />

        <MonitoredBloggers refreshKey={stats} />

        <HistoryTabs allTrends={allTrends} publishedVideos={publishedVideos} />

        <footer className="pt-10 pb-6 text-center text-xs text-[color:var(--bcs-text-dim)] font-mono-ui tracking-wider">
          BERTRAND CHIC SAUMON · AGENT IA · VEILLE RÉELLE FACEBOOK/TIKTOK 07:00 UTC · APIFY · CLAUDE SONNET 5.5 · MONTAGE + VOIX OFF
        </footer>
      </main>
    </>
  );
}
